import type { TrackingMode } from '@onflow/shared';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useOnboardingProfile, useUpdateTrackingModes } from '../../hooks/useOnboarding.js';
import { toast } from '../../lib/toast.js';

const OPTIONS: Array<{ value: TrackingMode; title: string; body: string }> = [
  {
    value: 'nutrition',
    title: 'Nutrition tracking',
    body: 'Get macro ranges and log foods from the verified catalog.',
  },
  {
    value: 'training',
    title: 'Training tracking',
    body: 'Choose or build a routine, schedule it, and log workouts.',
  },
];

export function ModeScreen() {
  const navigate = useNavigate();
  const profile = useOnboardingProfile();
  const update = useUpdateTrackingModes();
  const initial = useMemo(
    (): TrackingMode[] =>
      (profile.data?.trackingModes as TrackingMode[] | undefined) ?? ['nutrition', 'training'],
    [profile.data?.trackingModes],
  );
  const [modes, setModes] = useState<TrackingMode[]>(initial);

  useEffect(() => {
    setModes(initial);
  }, [initial]);

  function toggle(mode: TrackingMode) {
    setModes((current) =>
      current.includes(mode) ? current.filter((item) => item !== mode) : [...current, mode],
    );
  }

  async function next() {
    if (modes.length === 0) {
      toast.error('Choose nutrition, training, or both');
      return;
    }
    try {
      await update.mutateAsync({ modes });
      navigate('/onboarding/goal');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not save your tracking mode');
    }
  }

  return (
    <section className="onboarding-screen">
      <div className="onboarding-card onboarding-form">
        <h2 className="onboarding-title">What do you want to track?</h2>
        <p className="onboarding-subtitle">Choose one or both. You can change this later.</p>
        <fieldset className="form-group checkbox-group">
          <legend className="form-legend">Tracking modes</legend>
          {OPTIONS.map((option) => (
            <label
              key={option.value}
              className="checkbox-option"
              htmlFor={`mode-${option.value}`}
              aria-label={option.title}
            >
              <input
                id={`mode-${option.value}`}
                type="checkbox"
                checked={modes.includes(option.value)}
                onChange={() => toggle(option.value)}
              />
              <span>
                <strong>{option.title}</strong>
                <small>{option.body}</small>
              </span>
            </label>
          ))}
        </fieldset>
        <div className="onboarding-actions">
          <span />
          <button type="button" className="btn-primary" onClick={next} disabled={update.isPending}>
            {update.isPending ? 'Saving…' : 'Next'}
          </button>
        </div>
      </div>
    </section>
  );
}
